import React from 'react';
import { Box, Button, Grid, GridItem } from '@drone-js/core';
import { useSelector } from 'react-redux';
import { selectCmsFromBootstrap } from 'store/slice/bootstrap';
import CarouselImage from 'component/page/detail/DetailCard/ActivityCarousel/ActivityCarouselImage';
import { Image as ImageType } from 'gql/autogen/Image';
import { measureFinishRender } from 'core/Performance/PerformanceService';
import { selectPageTypeId } from 'store/slice/navigation';
import MainImage from 'component/page/detail/DetailCard/ActivityCarousel/MainImage';

const MAINIMAGEHEIGHT = '450px';
const THUMBNAILHEIGHT = '223px';

interface MosaicProps {
    imagesLength: number;
    mosaicImages: ImageType[];
    onImageClick: (e: React.MouseEvent, index: number, isViewAllImages: boolean) => void;
    isLessThanGallerySize?: boolean;
    shouldShowTextOverlay?: boolean;
    onImagesLoaded?: () => void;
}

interface Props {
    onImageClick: (e: React.MouseEvent, index: number, isViewAllImages: boolean) => void;
}

const ViewAllImages: React.FC<Props> = React.memo((props) => {
    const cms = useSelector(selectCmsFromBootstrap);
    return (
        <Box position="absolute" bottom="0" right="0" pb="16" pe="8">
            <Button
                data-element-name="activities-view-all-images-button"
                data-testid="activities-view-all-images-button"
                variant="label"
                hierarchy="secondary"
                label={cms[336900]}
                leadingIcon="fill.symbol.photo"
                onClick={(event) => props.onImageClick(event, 0, true)}
            />
        </Box>
    );
});
ViewAllImages.displayName = 'ViewAllImages';

const DefaultImage: React.FC<MosaicProps> = React.memo((props) => {
    return (
        <Box data-testid="image-carousel" position="relative" width="100%" height={MAINIMAGEHEIGHT} p="0" borderRadius="16" overflow="hidden">
            <Box
                data-element-name="activities-mosaic-gallery"
                data-testid="activities-mosaic-gallery"
                as="button"
                p="0"
                data-element-index={1}
                width="100%"
                height="100%"
                overflow="hidden"
                onClick={(event) => props.onImageClick(event, 0, false)}
            >
                <MainImage image={props.mosaicImages[0]} imageIndex={1} totalImages={1} onLoad={props.onImagesLoaded} isMosaicEnabled={true} height={MAINIMAGEHEIGHT} />
            </Box>
            {props.imagesLength && props.imagesLength > 1 && <ViewAllImages onImageClick={(event) => props.onImageClick(event, 0, true)} />}
        </Box>
    );
});
DefaultImage.displayName = 'DefaultImage';

interface ThumbnailItemProps extends Pick<MosaicProps, 'onImageClick' | 'onImagesLoaded' | 'shouldShowTextOverlay'> {
    image: ImageType;
    index: number;
    imagesLength: number;
}

const ThumbnailItem: React.FC<ThumbnailItemProps> = React.memo(({ image, index, imagesLength, onImageClick, onImagesLoaded, shouldShowTextOverlay }) => {
    const showOverlay = index === imagesLength - 2 && shouldShowTextOverlay;
    return (
        <GridItem key={index} lg={12} md={12} sm={12}>
            <Box position="relative" display="flex" alignItems="center" justifyContent="center" width="100%" height={THUMBNAILHEIGHT}>
                <Box
                    as="button"
                    p="0"
                    width="100%"
                    height="100%"
                    data-element-name="activities-mosaic-gallery"
                    data-testid="activities-mosaic-gallery"
                    data-element-index={index + 2}
                    overflow="hidden"
                    onClick={(event) => onImageClick(event, index + 1, false)}
                >
                    <CarouselImage
                        image={image}
                        imageIndex={index + 2}
                        totalImages={imagesLength}
                        onLoad={onImagesLoaded}
                        isMosaicEnabled={true}
                        objectFit="cover"
                        height={THUMBNAILHEIGHT}
                    />
                </Box>
                {showOverlay && imagesLength >= 3 && <ViewAllImages onImageClick={(event) => onImageClick(event, 0, true)} />}
            </Box>
        </GridItem>
    );
});
ThumbnailItem.displayName = 'ThumbnailItem';

const MosaicThumbnails: React.FC<MosaicProps> = React.memo((props) => (
    <>
        {props.mosaicImages.slice(1).map((image, i) => (
            <ThumbnailItem
                key={i}
                image={image}
                index={i}
                imagesLength={props.mosaicImages.length}
                onImageClick={props.onImageClick}
                onImagesLoaded={props.onImagesLoaded}
                shouldShowTextOverlay={props.shouldShowTextOverlay}
            />
        ))}
    </>
));
MosaicThumbnails.displayName = 'MosaicThumbnails';

const MultipleImage: React.FC<MosaicProps> = React.memo((props) => {
    return (
        <Box borderRadius="16" overflow="hidden">
            <Grid data-testid="multiple-image-carousel" spacing="4" fluid={true}>
                <GridItem lg={9} md={12} sm={12}>
                    <Box
                        as="button"
                        p="0"
                        data-element-name="activities-mosaic-gallery"
                        data-testid="activities-mosaic-gallery"
                        data-element-index={1}
                        width="100%"
                        overflow="hidden"
                        onClick={(event) => props.onImageClick(event, 0, false)}
                    >
                        <MainImage
                            image={props.mosaicImages[0]}
                            imageIndex={1}
                            totalImages={props.mosaicImages.length}
                            onLoad={props.onImagesLoaded}
                            isMosaicEnabled={true}
                            height={MAINIMAGEHEIGHT}
                        />
                    </Box>
                </GridItem>
                <GridItem lg={3} md={12} sm={12}>
                    <Box height="450px">
                        <Grid spacing="4" fluid={true}>
                            <MosaicThumbnails {...props} />
                        </Grid>
                    </Box>
                </GridItem>
            </Grid>
        </Box>
    );
});
MultipleImage.displayName = 'MultipleImage';

const MosaicComponent: React.FC<MosaicProps> = React.memo((props) => {
    const pageTypeId = useSelector(selectPageTypeId);
    let imagesLoaded = 0;

    const onImagesLoaded = () => {
        imagesLoaded += 1;
        if (imagesLoaded >= props.mosaicImages.length) {
            // Send measurement when all images are loaded on landing
            measureFinishRender({ component: 'MosaicGallery' }, pageTypeId);
        }
    };

    if (props.mosaicImages.length > 2) {
        return (
            <MultipleImage
                imagesLength={props.imagesLength}
                mosaicImages={props.mosaicImages}
                onImageClick={props.onImageClick}
                shouldShowTextOverlay={props.shouldShowTextOverlay}
                onImagesLoaded={onImagesLoaded}
                isLessThanGallerySize={props.isLessThanGallerySize}
            />
        );
    }
    return (
        <DefaultImage
            imagesLength={props.imagesLength}
            mosaicImages={props.mosaicImages}
            onImageClick={props.onImageClick}
            shouldShowTextOverlay={false}
            onImagesLoaded={onImagesLoaded}
        />
    );
});

MosaicComponent.displayName = 'MosaicComponent';
export default MosaicComponent;
